<template>
  <div class="ocr-report-tables-panel">
    <v-alert v-if="!filename" type="info" variant="tonal" density="compact">
      Select a report first.
    </v-alert>
    <template v-else>
      <div class="ocr-toolbar ocr-toolbar--stack">
        <div class="ocr-toolbar-row ocr-report-tables-toolbar">
          <div class="ocr-field ocr-report-map-type-select">
            <label class="profile-menu-label">Table type</label>
            <v-select
              v-model="typeFilter"
              :items="typeFilterItems"
              :menu-props="selectMenuProps"
              density="comfortable"
              variant="outlined"
              hide-details
              menu-icon="mdi-chevron-down"
            />
          </div>
          <v-btn-toggle
            v-model="viewMode"
            class="ocr-report-view-toggle"
            density="comfortable"
            variant="outlined"
            divided
            mandatory
          >
            <v-btn value="data" class="ocr-toolbar-btn">Data</v-btn>
            <v-btn value="image" class="ocr-toolbar-btn">Image</v-btn>
            <v-btn value="split" class="ocr-toolbar-btn">Both</v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <p v-if="!tables.length" class="ocr-empty-cell">No tables in this report.</p>
      <p v-else-if="!filteredTables.length" class="ocr-empty-cell">No tables match this type filter.</p>
      <p v-else-if="selectedTableIndex == null" class="ocr-empty-cell">Select a table in the sidebar.</p>
      <div v-else class="ocr-report-tables-view" :class="`ocr-report-tables-view--${viewMode}`">
        <div v-if="viewMode !== 'image'" class="ocr-report-table-editor">
          <p class="ocr-report-table-editor-title">{{ tableTitle }}</p>
          <p v-if="actionMessage" class="ocr-empty-cell">{{ actionMessage }}</p>
          <OcrEditableGrid
            :grid="editGrid"
            :saving="savingEdits"
            :ignored="ignored"
            @save="saveEditedGrid"
            @reset="resetTable"
            @split="splitTable"
            @duplicate-table="duplicateTable"
            @update:ignored="setIgnored"
          />
        </div>
        <OcrReportPagePreview
          v-if="viewMode !== 'data'"
          :filename="filename"
          :page-index="pageIndex"
          :max-page="maxPageIndex"
          :table-index="selectedTableIndex"
          :highlight-tables="true"
          :active-table-index="selectedTableIndex"
          class="ocr-report-table-image"
          @update:page-index="$emit('go-page', $event)"
        />
      </div>

      <div class="ocr-panel ocr-add-form">
        <div class="ocr-panel-title">Add missed table</div>
        <p class="ocr-upload-hint">Creates an empty table on the current PDF page so you can type or paste values.</p>
        <div class="ocr-form-grid ocr-form-grid--stacked">
          <div class="ocr-field">
            <label class="profile-menu-label">Table type</label>
            <v-select
              v-model="newType"
              :items="addTypeItems"
              :menu-props="selectMenuProps"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
          <div class="ocr-field">
            <label class="profile-menu-label">Rows</label>
            <v-text-field
              v-model.number="newRows"
              type="number"
              min="1"
              max="200"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
        </div>
        <div class="ocr-panel-actions">
          <span class="ocr-empty-cell">Page {{ (pageIndex ?? 0) + 1 }}</span>
          <v-btn size="small" color="primary" variant="flat" class="ocr-toolbar-btn" :loading="adding" @click="addTable">
            Add table
          </v-btn>
        </div>
        <v-alert v-if="addMessage" class="mt-2" :type="addType" variant="tonal" density="compact">
          {{ addMessage }}
        </v-alert>
      </div>
    </template>
  </div>
</template>

<script>
import ocrApi, { formatOcrApiError } from "@/services/ocrApi";
import OcrEditableGrid from "@/components/layout/OCR/OcrEditableGrid.vue";
import OcrReportPagePreview from "@/components/layout/OCR/OcrReportPagePreview.vue";

export default {
  name: "OcrReportTablesPanel",
  components: { OcrEditableGrid, OcrReportPagePreview },
  props: {
    filename: { type: String, default: null },
    tables: { type: Array, default: () => [] },
    tableTypes: { type: Array, default: () => [] },
    selectedTableIndex: { type: Number, default: null },
    tableDetail: { type: Object, default: null },
    pageIndex: { type: Number, default: 0 },
    maxPageIndex: { type: Number, default: 0 },
  },
  emits: ["select-table", "go-page", "added", "updated"],
  data() {
    return {
      typeFilter: "All",
      viewMode: "split",
      newType: "Unknown",
      newRows: 10,
      adding: false,
      savingEdits: false,
      actionMessage: "",
      addMessage: "",
      addType: "info",
      selectMenuProps: { contentClass: "ocr-select-menu" },
    };
  },
  computed: {
    typeFilterItems() {
      const types = [...new Set(this.tables.map((t) => t.table_type || "Unknown"))].sort();
      return ["All", ...types];
    },
    addTypeItems() {
      const fromCfg = (this.tableTypes || []).filter(Boolean);
      return ["Unknown", ...fromCfg.filter((t) => t !== "Unknown")];
    },
    filteredTables() {
      if (this.typeFilter === "All") return this.tables;
      return this.tables.filter((t) => (t.table_type || "Unknown") === this.typeFilter);
    },
    tableTitle() {
      if (!this.tableDetail) return "Table data";
      const page = (this.tableDetail.page ?? this.pageIndex ?? 0) + 1;
      const type = this.tableDetail.table_type || "Unknown";
      return `Page ${page} — ${type}`;
    },
    editGrid() {
      return this.tableDetail?.edited || this.tableDetail?.raw || { columns: [], data: [] };
    },
    ignored() {
      return this.tableDetail?.table_status === "Ignore";
    },
  },
  watch: {
    typeFilter() {
      const rows = this.filteredTables;
      if (!rows.length) return;
      if (!rows.some((t) => t.index === this.selectedTableIndex)) {
        this.$emit("select-table", rows[0].index);
      }
    },
  },
  methods: {
    tableUrl(suffix = "") {
      return `api/v1/reports/${encodeURIComponent(this.filename)}/tables/${this.selectedTableIndex}${suffix}`;
    },
    async saveEditedGrid(grid) {
      this.savingEdits = true;
      this.actionMessage = "";
      try {
        await ocrApi.put(this.tableUrl(), { grid });
        this.actionMessage = "Edits saved.";
        this.$emit("updated");
      } catch (e) {
        this.actionMessage = formatOcrApiError(e, "Could not save edits.");
      } finally {
        this.savingEdits = false;
      }
    },
    async resetTable() {
      this.actionMessage = "";
      try {
        await ocrApi.post(this.tableUrl("/reset"));
        this.actionMessage = "Table reset to the original extraction.";
        this.$emit("updated");
      } catch (e) {
        this.actionMessage = formatOcrApiError(e, "Could not reset the table.");
      }
    },
    async splitTable(rows) {
      this.actionMessage = "";
      try {
        const { data } = await ocrApi.post(this.tableUrl("/split"), { rows });
        this.actionMessage = "Selected rows moved into a new table.";
        this.$emit("added", data.index);
      } catch (e) {
        this.actionMessage = formatOcrApiError(e, "Could not split the table.");
      }
    },
    async duplicateTable() {
      this.actionMessage = "";
      try {
        const { data } = await ocrApi.post(this.tableUrl("/duplicate"));
        this.actionMessage = "Table duplicated.";
        this.$emit("added", data.index);
      } catch (e) {
        this.actionMessage = formatOcrApiError(e, "Could not duplicate the table.");
      }
    },
    async setIgnored(ignored) {
      this.actionMessage = "";
      try {
        await ocrApi.post(this.tableUrl("/ignore"), { ignored: !!ignored });
        this.actionMessage = ignored
          ? "Table ignored. It stays out of mapping until this is unchecked."
          : "Table included in mapping again.";
        this.$emit("updated");
      } catch (e) {
        this.actionMessage = formatOcrApiError(e, "Could not update ignore.");
      }
    },
    async addTable() {
      if (!this.filename) return;
      this.adding = true;
      this.addMessage = "";
      try {
        const { data } = await ocrApi.post(
          `api/v1/reports/${encodeURIComponent(this.filename)}/tables`,
          {
            page: this.pageIndex || 0,
            table_type: this.newType || "Unknown",
            num_rows: this.newRows || 10,
          },
        );
        this.addMessage = "Empty table added on this page.";
        this.addType = "success";
        this.$emit("added", data.table?.index);
      } catch (e) {
        this.addMessage = formatOcrApiError(e, "Could not add table.");
        this.addType = "error";
      } finally {
        this.adding = false;
      }
    },
  },
};
</script>
